import {
  useCallback,
  type FunctionComponent,
  type SyntheticEvent,
} from 'react';
import { FormattedMessage } from 'react-intl';

import AddIcon from '@atlaskit/icon/core/add';
import type { SourceType } from '@trello/analytics-types';
import { Analytics } from '@trello/atlassian-analytics';
import { dontUpsell } from '@trello/browser';
import {
  freeBoardsOver,
  freeBoardsRemaining,
  isCloseToFreeBoardLimit,
} from '@trello/business-logic/organization';
import { useWorkspaceId } from '@trello/id-context';
import { getScreenFromUrl } from '@trello/marketing-screens';
import type { ButtonProps } from '@trello/nachos/button';
import { PopoverPlacement, usePopover } from '@trello/nachos/popover';
import type { CreateBoardTestIds } from '@trello/test-ids';
import { getTestId } from '@trello/test-ids';
import { token } from '@trello/theme';

import { CreateBoardPopover } from 'app/src/components/CreateBoardPopover';
import { useCreateBoardTileLargeFragment } from './CreateBoardTileLargeFragment.generated';
import { IconListTile } from './IconListTile';

import styles from './CreateBoardListTile.less';

interface CreateBoardListTileProps extends ButtonProps {
  className?: string;
  analyticsSource?: SourceType;
}

export const CreateBoardListTile: FunctionComponent<
  CreateBoardListTileProps
> = ({ className, analyticsSource, ...props }) => {
  const { triggerRef, toggle, show, hide, popoverProps, push, pop } =
    usePopover({
      placement: PopoverPlacement.RIGHT_START,
    });

  const workspaceId = useWorkspaceId();

  const newBoard = useCallback(
    (e: SyntheticEvent<HTMLAnchorElement>) => {
      e.preventDefault();
      toggle();
      Analytics.sendUIEvent({
        action: 'clicked',
        actionSubject: 'tile',
        actionSubjectId: 'createBoardTile',
        source: analyticsSource ?? getScreenFromUrl(),
        containers: {
          workspace: {
            id: workspaceId,
          },
        },
      });
    },
    [analyticsSource, toggle, workspaceId],
  );

  const { data: workspace } = useCreateBoardTileLargeFragment({
    from: { id: workspaceId },
  });

  const boardsRemaining = workspace ? freeBoardsRemaining(workspace) : 0;
  const boardsOverLimit = workspace ? freeBoardsOver(workspace) : 0;
  const isApproachingLimit = workspace
    ? isCloseToFreeBoardLimit(workspace)
    : false;
  const shouldShowRemainingCount =
    !dontUpsell() && (isApproachingLimit || boardsOverLimit > 0);

  return (
    <div className={className}>
      <IconListTile
        onClick={newBoard}
        href="#"
        ref={triggerRef}
        testId={getTestId<CreateBoardTestIds>('create-board-tile')}
        icon={
          <AddIcon
            label=""
            color={token('color.icon.accent.gray', '#758195')}
          />
        }
        role="button"
        {...props}
      >
        <span>
          <FormattedMessage
            id="templates.board_list_item_add.create-new-board"
            defaultMessage="Create new board"
            description="Create new board button text"
          />
        </span>
        {shouldShowRemainingCount && (
          <small className={styles.remainingCount}>
            {'('}
            <FormattedMessage
              id="templates.board_list_item_add.count-remaining"
              defaultMessage="{boardsRemaining} remaining"
              description="Remaining free boards count"
              values={{ boardsRemaining }}
            />
            {boardsOverLimit > 0 && (
              <span>
                {' | '}
                <FormattedMessage
                  id="templates.board_list_item_add.boards-over"
                  defaultMessage="{boardsOverLimit, plural, =1 {1 board over} other {# boards over}}"
                  description="Number of boards over the free board limit"
                  values={{ boardsOverLimit }}
                />
              </span>
            )}
            {')'}
          </small>
        )}
      </IconListTile>
      <CreateBoardPopover
        analyticsSource="workspaceBoardsScreen"
        show={show}
        hide={hide}
        pop={pop}
        push={push}
        popoverProps={popoverProps}
        preSelectedWorkspaceId={workspaceId ?? undefined}
      />
    </div>
  );
};
